import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useContext, useEffect, useState } from "react";
import { useUndoHstImportBatchMutation } from "store/queries/integration";
import styled from "styled-components";
import { IHstImportBatchSummary } from "types/HstImportBatch";

import {
  apiErrorMessage,
  formatDateTime,
  isAccountNameMatch,
  userDisplayName,
} from "../../utils/hstImports";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
  max-width: 560px;
  font-size: 14px;
`;

const DangerBox = styled.div`
  padding: ${sizer(3)};
  background-color: var(--red-50);
  border: 1px solid var(--red-300);
  border-radius: 4px;
  color: var(--red-800);

  ul {
    margin: ${sizer(2)} 0 0;
    padding-left: ${sizer(5)};
  }
`;

const BlockerBox = styled.div`
  padding: ${sizer(3)};
  background-color: var(--yellow-50);
  border: 1px solid var(--yellow-300);
  border-radius: 4px;
  color: var(--yellow-900);

  ul {
    margin: ${sizer(2)} 0 0;
    padding-left: ${sizer(5)};
  }
`;

interface IUndoHstImportDialogProps {
  batch: IHstImportBatchSummary | null;
  onHide: () => void;
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

export function UndoHstImportDialog({
  batch,
  onHide,
}: IUndoHstImportDialogProps) {
  const toastRef = useContext(ToastContext);
  const [typedName, setTypedName] = useState("");
  const [serverBlockers, setServerBlockers] = useState<string[]>([]);
  const [undoImport, { isLoading }] = useUndoHstImportBatchMutation();

  useEffect(() => {
    setTypedName("");
    setServerBlockers([]);
  }, [batch?.id]);

  if (!batch) return null;

  const blockers = serverBlockers.length ? serverBlockers : batch.undoBlockers;
  const blocked =
    !batch.undoable || batch.status !== "Active" || blockers.length > 0;
  const nameMatches = isAccountNameMatch(typedName, batch.accountName);
  const canConfirm = !blocked && nameMatches && !isLoading;

  const handleHide = () => {
    if (isLoading) return;
    onHide();
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      const result = await undoImport({
        id: batch.id,
        confirmAccountName: typedName.trim(),
      }).unwrap();
      const deletedOrders =
        result.data.undoSummary?.deletedCounts?.order ?? batch.createdRowCount;
      toastRef?.current?.show({
        severity: "success",
        summary: "Import undone",
        detail: `Deleted ${plural(
          deletedOrders,
          "estimate",
          "estimates"
        )} from ${batch.accountName}.`,
        life: 6000,
      });
      onHide();
    } catch (error) {
      const { message, blockers: errorBlockers } = apiErrorMessage(
        error,
        "The import could not be undone."
      );
      if (errorBlockers.length) setServerBlockers(errorBlockers);
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't undo the import",
        detail: errorBlockers.length
          ? `${message} ${errorBlockers.join(" ")}`
          : message,
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Keep the import"
        className="p-button-text"
        onClick={handleHide}
        disabled={isLoading}
      />
      <Button
        label="Delete this import"
        icon="pi pi-trash"
        className="p-button-danger"
        onClick={handleConfirm}
        disabled={!canConfirm}
        loading={isLoading}
        data-testid="undo-hst-import-confirm"
      />
    </div>
  );

  return (
    <Dialog
      header="Undo HST import"
      visible
      onHide={handleHide}
      footer={footer}
      closable={!isLoading}
      style={{ width: "600px" }}
    >
      <Body>
        <div>
          Imported into <strong>{batch.accountName}</strong> on{" "}
          {formatDateTime(batch.createdAt)}
          {batch.importedBy ? ` by ${userDisplayName(batch.importedBy)}` : ""}.
        </div>

        <DangerBox>
          <strong>This permanently deletes data. It cannot be reversed.</strong>
          <ul>
            <li>
              {plural(
                batch.createdRowCount,
                "estimate this import created",
                "estimates this import created"
              )}{" "}
              will be deleted, with their patients, quotes and eligibility
              checks.
            </li>
            <li>
              {plural(
                batch.updatedCount,
                "existing estimate this import updated",
                "existing estimates this import updated"
              )}{" "}
              will be left as they are. The update is not rolled back.
            </li>
          </ul>
        </DangerBox>

        {blocked && (
          <BlockerBox data-testid="undo-hst-import-blockers">
            <strong>This import cannot be undone.</strong>
            {blockers.length > 0 ? (
              <ul>
                {blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            ) : (
              <div>
                {batch.status === "Undone"
                  ? "It has already been undone."
                  : "It is outside the undo window."}
              </div>
            )}
          </BlockerBox>
        )}

        <label htmlFor="undo-hst-import-account-name">
          Type the account name <strong>{batch.accountName}</strong> to confirm.
        </label>
        <InputText
          id="undo-hst-import-account-name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          disabled={blocked || isLoading}
          autoComplete="off"
          placeholder={batch.accountName}
        />
      </Body>
    </Dialog>
  );
}
